/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */
import React from 'react';
import { Box, Divider, Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import {
  Link as RouterLink,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import LogoSvg from 'components/ChoreoSystem/Images/Various/LogoBlack.svg';
import Link from 'components/ChoreoSystem/Link/Link';
import Tooltip from 'components/ChoreoSystem/Tooltip/Tooltip';
import {
  choreoHomePage,
  privacyPolicy,
  termsOfUse,
  availableLoginRegions,
  enableAsgardeoMaintenanceFlag,
  hideSignupEntryPoints,
  PRODUCT_NAME,
} from 'config';
import { InvitationVerifiedMessage } from 'modules/invitation/InvitationMessages';
import IdentityProviderInfo from 'modules/login/IdentityProviderInfo/IdentityProviderInfo';
import RegionSelector from 'modules/login/RegionSelector';
import { EMBEDDED_SIGN_UP_CONTEST, EMBEDDED_SIGN_UP_PATH } from 'utils/route';
import useStyles from './AuthenticationFormCard.styles';

interface AuthenticationFormCardProps {
  children: React.ReactNode;
  title: React.ReactNode;
  testId: string;
  isMobile: boolean;
  orgName?: string;
  isInvitation?: boolean;
  isEmailLogin?: boolean;
  isSignUp?: boolean;
  hideRegionSelector?: boolean;
}

function AuthenticationFormCard(props: AuthenticationFormCardProps) {
  const {
    children,
    title,
    testId,
    orgName,
    isMobile,
    isInvitation = false,
    isEmailLogin,
    isSignUp = false,
    hideRegionSelector,
  } = props;
  const classes = useStyles();
  const isEmbeddedSignup = useRouteMatch(EMBEDDED_SIGN_UP_PATH);
  const isEmbeddedContestSignup = useRouteMatch(EMBEDDED_SIGN_UP_CONTEST);
  const isEmbeddedIframe = isEmbeddedSignup || isEmbeddedContestSignup;
  const history = useHistory();
  const location = useLocation();

  return (
    <Box className={classes.authenticationFormCard} data-cyid={testId}>
      {isMobile && (
        <Box mb={2}>
          <img
            alt={`${PRODUCT_NAME} Logo`}
            className={classes.choreoLogo}
            src={LogoSvg}
          />
        </Box>
      )}
      {isInvitation && orgName && (
        <Box mb={2}>
          <InvitationVerifiedMessage orgName={orgName} />
        </Box>
      )}
      {isEmbeddedIframe ? (
        <>
          <Box mb={5}>
            <Typography variant="h2">{title}</Typography>
          </Box>
          {availableLoginRegions.length > 1 && !hideRegionSelector && (
            <RegionSelector
              availableRegions={availableLoginRegions}
              showRegionLabel
            />
          )}
        </>
      ) : (
        <Box
          mb={5}
          display="flex"
          justifyContent={
            availableLoginRegions.length > 1 && !hideRegionSelector
              ? 'space-between'
              : 'center'
          }
          alignItems="center"
          width="100%"
        >
          <Typography variant="h2">{title}</Typography>
          {availableLoginRegions.length > 1 && !hideRegionSelector && (
            <RegionSelector availableRegions={availableLoginRegions} />
          )}
        </Box>
      )}

      <Box
        className={
          isEmbeddedIframe
            ? classes.authenticationFormCardContentEmbedded
            : classes.authenticationFormCardContent
        }
      >
        {children}
      </Box>
      <Box mt={3}>
        {((isEmailLogin && !hideSignupEntryPoints) || isSignUp) && (
          <Box mb={3}>
            {isEmailLogin && !hideSignupEntryPoints && (
              <Typography variant="body1" className={classes.choreoSignUpLink}>
                <FormattedMessage
                  id="components.ChoreoSystem.Authenticate.AuthenticationFormCard.accountExists"
                  defaultMessage="Don't have an account?&nbsp;"
                />

                <Tooltip
                  disabled={!enableAsgardeoMaintenanceFlag}
                  title={
                    <FormattedMessage
                      id="components.ChoreoSystem.Authenticate.AuthenticationFormCard.maintenance.tooltip"
                      defaultMessage={
                        'New registrations are temporarily unavailable during this period ' +
                        'due to an ongoing maintenance. Please come back and try again shortly.'
                      }
                    />
                  }
                  arrow
                >
                  <span>
                    <Link
                      underline="none"
                      disabled={enableAsgardeoMaintenanceFlag}
                      component={RouterLink}
                      role="button"
                      tabIndex={0}
                      rel="noopener"
                      to={{ pathname: '/signup' }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        history.push('/signup', location.state);
                      }}
                      testId={`${testId}-signup`}
                    >
                      <FormattedMessage
                        id="components.ChoreoSystem.Authenticate.AuthenticationFormCard.signup"
                        defaultMessage="Sign Up"
                      />
                    </Link>
                  </span>
                </Tooltip>
              </Typography>
            )}
            {isSignUp && (
              <Typography variant="body1" className={classes.choreoSignUpLink}>
                <FormattedMessage
                  id="modules.signup.signupCard.signupCard.accountExists"
                  defaultMessage="Already have an account?&nbsp;"
                />
                {isEmbeddedIframe ? (
                  <Link
                    underline="none"
                    component={RouterLink}
                    rel="noopener"
                    to="/"
                    testId={`${testId}-signin`}
                    target="_blank"
                  >
                    <FormattedMessage
                      id="modules.signup.signupCard.signupCard.signin"
                      defaultMessage="Sign In"
                    />
                  </Link>
                ) : (
                  <Link
                    underline="none"
                    component={RouterLink}
                    rel="noopener"
                    to="/login"
                    testId={`${testId}-signin`}
                  >
                    <FormattedMessage
                      id="modules.signup.signupCard.signupCard.signin"
                      defaultMessage="Sign In"
                    />
                  </Link>
                )}
              </Typography>
            )}
          </Box>
        )}
        {!isEmbeddedIframe && (
          <>
            <Box>
              <Typography variant="body1" className={classes.choreMoreDetails}>
                <FormattedMessage
                  id="components.ChoreoSystem.Authenticate.AuthenticationFormCard.moreDetails.text"
                  defaultMessage="More details at&nbsp;"
                />
                <Link
                  underline="none"
                  component={RouterLink}
                  rel="noopener"
                  target="_blank"
                  to={{ pathname: choreoHomePage }}
                  variant="body1"
                  testId={`${testId}-choreo-homepage`}
                >
                  <FormattedMessage
                    id="components.ChoreoSystem.Authenticate.AuthenticationFormCard.moreDetails.linkText"
                    defaultMessage="wso2.com/developer-platform "
                  />
                </Link>
              </Typography>
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="center"
              mt={0.5}
            >
              <Box mr={1}>
                <Link
                  underline="none"
                  component={RouterLink}
                  target="_blank"
                  rel="noopener"
                  to={{ pathname: privacyPolicy }}
                  variant="body1"
                  className={classes.otherLinks}
                  testId={`${testId}-privacy-policy`}
                >
                  <FormattedMessage
                    id="layouts.main.components.Footer.privacy.policy"
                    defaultMessage="Privacy Policy"
                  />
                </Link>
              </Box>
              <Divider orientation="vertical" light flexItem />
              <Box ml={1}>
                <Link
                  underline="none"
                  component={RouterLink}
                  target="_blank"
                  rel="noopener"
                  to={{ pathname: termsOfUse }}
                  className={classes.otherLinks}
                  variant="body1"
                  testId={`${testId}-terms-of-use`}
                >
                  <FormattedMessage
                    id="layouts.main.components.Footer.terms.of.use"
                    defaultMessage="Terms of Use"
                  />
                </Link>
              </Box>
            </Box>
          </>
        )}
      </Box>
      <Box pt={4}>
        <IdentityProviderInfo />
      </Box>
    </Box>
  );
}

export default AuthenticationFormCard;
